/*
 * KALTA — Announcement bar (sections/announcement-bar.liquid).
 * Source: 04_marketing/content/artifacts/launch-banner/index.html, round 2 (VERDICT PUBLISH, Remy 2026-09-27; W-114).
 *
 * - Rotates every data-interval ms (section setting, default 5 s).
 * - Pause button (stays paused until play is pressed); pauses on hover and on focus; stops while the tab is hidden.
 * - aria-live="off" while rotating on its own, "polite" once the user takes over (WAI carousel pattern).
 * - Hidden slides are aria-hidden and their links leave the tab order.
 * - Fit: full line first, then each phone cut; each tried from the base size down to a 10.5 px floor. Never wraps.
 * - Reduced motion: slide 1, no rotation, every drawing in its finished state (CSS), pause button stays hidden.
 * - Theme editor: selecting a block shows that slide and holds it.
 *
 * 2026-09-27 (W-115) — "does not rotate by itself" (Remy, live). The W-114 pause flags LATCHED: each was set by
 * one event and cleared only by a partner event that real use does not reliably send.
 *   - hover: mouseenter also fires on a TAP (touch emulates mouse events) and no mouseleave follows until the
 *     next tap elsewhere, so one touch of the bar on a phone stopped rotation for the rest of the visit. On
 *     desktop, a cursor that left the window from the bar (it is the top 38 px, next to the tab strip) or a
 *     page restored from the back/forward cache kept hover=true with nothing to clear it.
 *   - focus: any CLICK on ←, → or a slide link left focus on that element (Chrome, Android), so focus=true held
 *     until the visitor clicked somewhere else. The first slide's link is /#oh-waitlist, a same-page anchor on
 *     the homepage: clicking it scrolled away and left the carousel frozen, and Back restored it frozen.
 *   A headless browser never hovers, taps or focuses, which is why Jimmy's run cycled.
 * Fix: hover pauses only for a real mouse pointer and is cleared when the pointer leaves the window, the
 * window blurs, the tab is hidden or the page is restored; focus pauses only for KEYBOARD focus
 * (:focus-visible, or a key-press heuristic where that selector is unsupported). Reduced motion is read live.
 */
(function () {
  'use strict';

  var FLOOR = 10.5;
  var RM = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  // Input modality, for browsers without :focus-visible. A key press means the next focus is keyboard focus;
  // a pointer press means it is not.
  var lastKey = false;
  document.addEventListener('keydown', function (e) {
    if (!e.metaKey && !e.altKey && !e.ctrlKey) lastKey = true;
  }, true);
  ['mousedown', 'pointerdown', 'touchstart'].forEach(function (t) {
    document.addEventListener(t, function () { lastKey = false; }, { capture: true, passive: true });
  });
  function keyboardFocus(el) {
    if (!el || el.nodeType !== 1) return false;
    try { return el.matches(':focus-visible'); } catch (e) { return lastKey; }
  }

  function init(root) {
    if (!root || root.__kb) return;
    var msgs = root.querySelector('.js-msgs');
    var slides = Array.prototype.slice.call(root.querySelectorAll('.js-message'));
    if (!msgs || !slides.length) return;

    var prevBtn = root.querySelector('.js-prev');
    var nextBtn = root.querySelector('.js-next');
    var pauseBtn = root.querySelector('.js-pause');
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 5000;
    var n = slides.length;
    var st = { i: 0, t: null, userPaused: false, hover: false, focus: false, hidden: false, editor: false };
    root.__kb = st;

    var reduced = function () { return !!RM.matches; };
    var rotating = function () {
      return n > 1 && !reduced() && !st.userPaused && !st.hover && !st.focus && !st.hidden && !st.editor;
    };

    function schedule() {
      clearTimeout(st.t);
      msgs.setAttribute('aria-live', rotating() ? 'off' : 'polite');
      if (rotating()) st.t = setTimeout(function () { go(st.i + 1); }, interval);
    }

    function go(k) {
      st.i = ((k % n) + n) % n;
      slides.forEach(function (el, j) {
        var on = j === st.i;
        el.classList.toggle('is-active', on);
        if (on) el.removeAttribute('aria-hidden'); else el.setAttribute('aria-hidden', 'true');
        Array.prototype.forEach.call(el.querySelectorAll('a'), function (a) {
          if (on) a.removeAttribute('tabindex'); else a.setAttribute('tabindex', '-1');
        });
      });
      schedule();
    }

    function fit() {
      var avail = msgs.clientWidth;
      if (!avail) return;
      slides.forEach(function (el) {
        var inner = el.querySelector('.kb-inner');
        var target = el.querySelector('.js-text');
        if (!inner || !target) return;
        var full = target.getAttribute('data-full') || target.textContent;
        var cands = [full];
        ['data-cut1', 'data-cut2'].forEach(function (a) { var v = target.getAttribute(a); if (v) cands.push(v); });
        el.style.fontSize = '';
        var cs = getComputedStyle(el);
        var pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
        var base = parseFloat(cs.fontSize);
        var over = function () { return inner.getBoundingClientRect().width > avail - pad + 0.5; };
        var used = null;
        for (var c = 0; c < cands.length && used === null; c++) {
          target.textContent = cands[c];
          el.style.fontSize = '';
          var fs = base;
          while (over() && fs > FLOOR) { fs -= 0.5; el.style.fontSize = fs + 'px'; }
          if (!over()) used = cands[c];
        }
        if (used === null) { used = cands[cands.length - 1]; target.textContent = used; el.style.fontSize = FLOOR + 'px'; }
        var link = target.closest ? target.closest('a') : null;
        if (link) {
          if (used !== full) link.setAttribute('aria-label', full); else link.removeAttribute('aria-label');
        }
      });
    }

    function setPaused(p) {
      st.userPaused = p;
      if (pauseBtn) {
        pauseBtn.classList.toggle('is-paused', p);
        pauseBtn.setAttribute('aria-label', p ? 'Play announcements' : 'Pause announcements');
      }
      schedule();
    }

    // Controls exist only once JS runs (they are `hidden` in the markup, so JS-off shows no dead buttons).
    if (n > 1) {
      if (prevBtn) { prevBtn.hidden = false; prevBtn.addEventListener('click', function () { go(st.i - 1); }); }
      if (nextBtn) { nextBtn.hidden = false; nextBtn.addEventListener('click', function () { go(st.i + 1); }); }
      if (pauseBtn) pauseBtn.addEventListener('click', function () { setPaused(!st.userPaused); });
    }
    // Reduced motion, read live (the OS setting can change while the page is open): slide 1, static, no pause button.
    function applyMotion() {
      var rm = reduced();
      root.classList.toggle('kb--rm', rm);
      if (pauseBtn) pauseBtn.hidden = rm || n <= 1;
      if (rm) go(0); else schedule();
    }
    if (RM.addEventListener) RM.addEventListener('change', applyMotion);
    else if (RM.addListener) RM.addListener(applyMotion);

    // Hover pauses for a real mouse only. A tap is not a hover: touch pointers are ignored, and without Pointer
    // Events the listener is attached only where the primary pointer can hover.
    var canHover = !window.matchMedia || window.matchMedia('(hover: hover)').matches;
    function setHover(v) { if (st.hover !== v) { st.hover = v; schedule(); } }
    if (window.PointerEvent) {
      root.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') setHover(true); });
      root.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') setHover(false); });
      // A resting mouse re-arms the pause after a safety net cleared it (no second pointerenter would come).
      root.addEventListener('pointermove', function (e) { if (e.pointerType === 'mouse') setHover(true); });
    } else if (canHover) {
      root.addEventListener('mouseenter', function () { setHover(true); });
      root.addEventListener('mouseleave', function () { setHover(false); });
    }
    // Safety nets: the pointer left the window, the window lost focus, or the page came back from the
    // back/forward cache. Each clears a hover that no leave event will ever clear.
    document.addEventListener('mouseout', function (e) { if (!e.relatedTarget) setHover(false); });
    window.addEventListener('blur', function () { setHover(false); });

    // Focus pauses for keyboard focus only (WAI carousel). A mouse click or tap on ←, →, pause or a slide link
    // leaves focus there without :focus-visible, so it no longer freezes the bar.
    root.addEventListener('focusin', function (e) { st.focus = keyboardFocus(e.target); schedule(); });
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) { st.focus = false; schedule(); }
    });

    document.addEventListener('visibilitychange', function () {
      st.hidden = document.hidden;
      if (!st.hidden) st.hover = false;
      schedule();
    });
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      st.hover = false;
      st.hidden = document.hidden;
      st.focus = root.contains(document.activeElement) && keyboardFocus(document.activeElement);
      schedule();
    });

    if (window.ResizeObserver) {
      var raf = 0;
      new ResizeObserver(function () {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(fit);
      }).observe(msgs);
    }

    // is-ready first: it releases the no-JS phone fallback (shortest cut at the floor), so fit() measures real sizes.
    root.classList.add('is-ready');
    fit();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    go(0);
    applyMotion();

    // Theme editor: show and hold the selected block's slide.
    st.select = function (el) {
      var k = slides.indexOf(el);
      if (k < 0) return;
      st.editor = true;
      go(k);
    };
    st.deselect = function () { st.editor = false; schedule(); };
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-testid="announcement-bar"]'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector && e.target.querySelector('[data-testid="announcement-bar"]');
    if (root) init(root);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target && e.target.closest && e.target.closest('[data-testid="announcement-bar"]');
    if (root && root.__kb && root.__kb.select) root.__kb.select(e.target);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var root = e.target && e.target.closest && e.target.closest('[data-testid="announcement-bar"]');
    if (root && root.__kb && root.__kb.deselect) root.__kb.deselect();
  });
})();
